import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  AlertTriangle, 
  Search, 
  Download, 
  Eye,
  FileText,
  CheckCircle2,
  User,
  Clock,
  LayoutDashboard,
  History,
  ChevronRight,
  ChevronDown,
  BarChart3,
  ShoppingCart,
  ShoppingBag,
  Scale,
  GitCompare
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import './ExceptionalBalance.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AnomalyResults = () => {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [expandedResults, setExpandedResults] = useState(new Set());

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API}/ml/analysis-results`);
      setResults(response.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load analysis results');
    } finally {
      setLoading(false);
    }
  };

  const formatAnalysisType = (type) => {
    const typeMap = {
      'purchase_reconciliation': 'Purchase Reconciliation',
      'sales_reconciliation': 'Sales Reconciliation',
      'exceptional_balance': 'Exceptional Balance',
      'opening_balance_scrutiny': 'Opening Balance Scrutiny'
    };
    return typeMap[type] || type;
  };

  const getTypeColor = (type) => {
    const colorMap = {
      'purchase_reconciliation': 'bg-indigo-50 text-indigo-700 border-indigo-200',
      'sales_reconciliation': 'bg-indigo-50 text-indigo-700 border-indigo-200',
      'exceptional_balance': 'bg-indigo-50 text-indigo-700 border-indigo-200',
      'opening_balance_scrutiny': 'bg-indigo-50 text-indigo-700 border-indigo-200'
    };
    return colorMap[type] || 'bg-slate-50 text-slate-700 border-slate-200';
  };

  const getTypeIcon = (type) => {
    const iconMap = {
      'purchase_reconciliation': <ShoppingCart className="h-5 w-5 text-indigo-600 group-hover:text-white transition-colors" />,
      'sales_reconciliation': <ShoppingBag className="h-5 w-5 text-indigo-600 group-hover:text-white transition-colors" />,
      'exceptional_balance': <Scale className="h-5 w-5 text-indigo-600 group-hover:text-white transition-colors" />,
      'opening_balance_scrutiny': <GitCompare className="h-5 w-5 text-indigo-600 group-hover:text-white transition-colors" />
    };
    return iconMap[type] || <FileText className="h-5 w-5 text-indigo-600 group-hover:text-white transition-colors" />;
  };

  const toggleExpanded = (resultId) => {
    setExpandedResults(prev => {
      const newSet = new Set(prev);
      if (newSet.has(resultId)) {
        newSet.delete(resultId);
      } else {
        newSet.add(resultId);
      }
      return newSet;
    });
  };

  // Render table from array of objects
  const renderTable = (data, title = '') => {
    if (!Array.isArray(data) || data.length === 0) {
      return <p className="text-sm text-gray-500">No data available</p>;
    }

    // Get all unique keys from all objects
    const allKeys = [...new Set(data.flatMap(item => Object.keys(item)))];
    
    return (
      <div className="mb-6">
        {title && <h5 className="font-bold text-slate-800 mb-3 text-sm uppercase tracking-tight">{title}</h5>}
        <div className="overflow-x-auto border border-slate-200 rounded-xl shadow-sm">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50 shadow-sm">
              <tr>
                <th className="px-4 py-3 text-left text-[11px] font-black text-slate-900 uppercase tracking-widest border-r border-slate-200">
                  #
                </th>
                {allKeys.map((key) => (
                  <th 
                    key={key}
                    className="px-4 py-3 text-left text-[11px] font-black text-slate-900 uppercase tracking-widest border-r border-slate-200 last:border-r-0"
                  >
                    {key.replace(/_/g, ' ')}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-100">
              {data.map((row, idx) => (
                <tr key={idx} className="hover:bg-slate-50/50 transition-colors">
                  <td className="px-4 py-3 text-xs text-slate-900 border-r border-slate-100 font-bold bg-slate-50/50">
                    {(idx + 1).toString().padStart(2, '0')}
                  </td>
                  {allKeys.map((key) => {
                    const value = row[key];
                    let displayValue = value;
                    let cellClass = "px-4 py-3 text-sm text-slate-900 border-r border-slate-100 last:border-r-0 font-medium";
                    
                    // Handle different value types
                    if (value === null || value === undefined) {
                      displayValue = '-';
                      cellClass += ' text-slate-400';
                    } else if (typeof value === 'object') {
                      displayValue = JSON.stringify(value);
                    } else if (typeof value === 'number') {
                      displayValue = value.toLocaleString();
                      cellClass += ' text-right font-mono text-slate-900';
                    } else {
                      displayValue = String(value);
                    }
                    
                    // Synchronized Remark/Status Badge Logic
                    const isStatusField = key.toLowerCase().includes('status') || key.toLowerCase().includes('remark') || key === 'symbol';
                    
                    if (isStatusField && displayValue !== '-') {
                      if (displayValue.includes('Found in') || displayValue.toLowerCase() === 'found' || displayValue.includes('✅')) {
                        return (
                          <td key={key} className={cellClass}>
                            <Badge variant="default" className="bg-emerald-600 text-white border-transparent text-[10px] whitespace-nowrap px-2.5 py-0.5 font-bold flex items-center gap-1 w-fit">
                              <CheckCircle2 className="h-3 w-3" /> Found
                            </Badge>
                          </td>
                        );
                      }
                      if (displayValue.includes('Not found') || displayValue.toLowerCase() === 'not found' || displayValue.includes('❌')) {
                        return (
                          <td key={key} className={cellClass}>
                            <Badge variant="destructive" className="text-[10px] whitespace-nowrap px-2.5 py-0.5 font-bold flex items-center gap-1 w-fit">
                              <AlertTriangle className="h-3 w-3" /> Not Found
                            </Badge>
                          </td>
                        );
                      }
                      if (displayValue.toLowerCase().includes('differ') || displayValue.includes('⚠️')) {
                        return (
                          <td key={key} className={cellClass}>
                            <Badge variant="secondary" className="bg-amber-100 text-amber-700 border-amber-200 text-[10px] whitespace-nowrap px-2.5 py-0.5 font-bold flex items-center gap-1 w-fit">
                              <AlertTriangle className="h-3 w-3" /> Differs
                            </Badge>
                          </td>
                        );
                      }
                    }
                    
                    return (
                      <td key={key} className={cellClass}>
                        {displayValue}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

// Hierarchical Table Component for Exceptional Balance
const HierarchicalTable = ({ data }) => {
  const [nestedData, setNestedData] = React.useState([]);
  const [columns, setColumns] = React.useState([]);

  React.useEffect(() => {
    if (data && data.length > 0) {
      const hierarchy = buildHierarchy(data);
      setNestedData(hierarchy);
      setColumns(Object.keys(data[0]));
    }
  }, [data]);

  const toggleRow = (row) => {
    row.expanded = !row.expanded;
    setNestedData([...nestedData]);
  };

  const formatCellValue = (columnName, value) => {
    if (value === null || value === undefined) return '-';
    if (columnName === 'opening_balance_type' || columnName === 'closing_balance_type') {
      if (value === 0) return 'Credit';
      if (value === 1) return 'Debit';
      return '-';
    }
    if (typeof value === 'number') return value.toLocaleString();
    return value;
  };

  const renderRows = (rows, level = 0) =>
    rows.map((row, index) => (
      <React.Fragment key={row.id || index}>
        <tr className={`group ${row.level === 0 ? 'bg-slate-50/50' : 'hover:bg-indigo-50/30 transition-colors'}`}>
          {columns.map((col) => {
            const isParticulars = col === 'particulars';
            return (
              <td
                key={col}
                className={`py-2 px-4 text-xs ${isParticulars ? 'font-bold text-slate-800' : 'text-slate-600 font-mono text-right'}`}
                style={isParticulars ? { paddingLeft: `${level * 24 + 16}px` } : {}}
              >
                <div className="flex items-center gap-2">
                    {isParticulars && row.subRows && row.subRows.length > 0 && (
                        <button
                          className="flex items-center justify-center h-4 w-4 rounded hover:bg-slate-200 transition-colors"
                          onClick={() => toggleRow(row)}
                        >
                          {row.expanded ? <ChevronDown className="h-3 w-3 text-slate-600" /> : <ChevronRight className="h-3 w-3 text-slate-600" />}
                        </button>
                    )}
                    <span className={isParticulars ? 'truncate max-w-xs' : ''}>
                        {formatCellValue(col, row[col])}
                    </span>
                </div>
              </td>
            );
          })}
        </tr>
        {row.subRows && row.subRows.length > 0 && row.expanded && renderRows(row.subRows, level + 1)}
      </React.Fragment>
    ));

  if (!data || data.length === 0) return null;

  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden shadow-sm">
      <table className="min-w-full divide-y divide-slate-200">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr>
            {columns.map((col) => (
              <th key={col} className={`px-4 py-3 text-[10px] font-black text-slate-900 uppercase tracking-widest ${col !== 'particulars' ? 'text-right' : 'text-left'}`}>
                {col.replace(/_/g, ' ')}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-slate-100">{renderRows(nestedData)}</tbody>
      </table>
    </div>
  );
};

  // Render data based on structure
  const renderTableData = (resultData, analysisType) => {
    if (!resultData) {
      return <p className="text-sm text-gray-500">No data available</p>;
    }

    // Special case: Exceptional Balance (Hierarchical)
    if (analysisType === 'exceptional_balance' && resultData.data && Array.isArray(resultData.data)) {
      return <HierarchicalTable data={resultData.data} />;
    }

    // Case 1: Direct array (Exceptional Balance format fallback or others)
    if (resultData.data && Array.isArray(resultData.data)) {
      return renderTable(resultData.data);
    }

    // Case 2: Object with multiple sections (Reconciliation format)
    if (typeof resultData === 'object' && !Array.isArray(resultData)) {
      const sections = Object.entries(resultData);
      
      if (sections.length === 0) {
        return <p className="text-sm text-gray-500">No data available</p>;
      }

      return (
        <div className="space-y-6">
          {sections.map(([sectionKey, sectionData]) => {
            if (Array.isArray(sectionData) && sectionData.length > 0) {
              // Format section title
              const sectionTitle = sectionKey
                .split('_')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ');
              
              return (
                <div key={sectionKey}>
                  {renderTable(sectionData, sectionTitle)}
                </div>
              );
            } else if (typeof sectionData === 'object' && sectionData !== null) {
              // Handle nested objects
              return (
                <div key={sectionKey} className="mb-4">
                  <h5 className="font-semibold text-gray-800 mb-2 text-sm">
                    {sectionKey.replace(/_/g, ' ')}
                  </h5>
                  <pre className="text-xs bg-gray-50 p-3 rounded border overflow-x-auto">
                    {JSON.stringify(sectionData, null, 2)}
                  </pre>
                </div>
              );
            }
            return null;
          })}
        </div>
      );
    }

    // Case 3: Direct array
    if (Array.isArray(resultData)) {
      return renderTable(resultData);
    }

    // Fallback to JSON
    return (
      <pre className="text-xs bg-gray-50 p-3 rounded border overflow-x-auto">
        {JSON.stringify(resultData, null, 2)}
      </pre>
    );
  };

  const filteredResults = results.filter(result => {
    const matchesSearch = !searchTerm || 
      result.file_names?.some(f => f.toLowerCase().includes(searchTerm.toLowerCase())) ||
      result.summary?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      result.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      result.analysis_type?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesType = selectedType === 'all' || result.analysis_type === selectedType;
    
    return matchesSearch && matchesType;
  });

  const analysisTypes = [
    { value: 'purchase_reconciliation', label: 'Purchase Reconciliation' },
    { value: 'sales_reconciliation', label: 'Sales Reconciliation' },
    { value: 'exceptional_balance', label: 'Exceptional Balance' },
    { value: 'opening_balance_scrutiny', label: 'Opening Balance Scrutiny' }
  ];

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-32 bg-gray-200 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="anomaly-results-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Analysis Results</h1>
          <p className="text-gray-600 mt-2">Review all saved analysis results</p>
        </div>
        <Button variant="outline" onClick={() => toast.info('Export feature coming soon!')}>
          <Download className="h-4 w-4 mr-2" />
          Export Report
        </Button>
      </div>

      {/* Summary Cards */}
      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        {[
          { label: 'Total Analyses', value: results.length, icon: History, sub: 'Historical records' },
          { label: 'Total Issues', value: results.reduce((sum, r) => sum + (r.issues_found || 0), 0), icon: AlertTriangle, color: 'text-rose-600', sub: 'Action required' },
          { label: 'Total Records', value: results.reduce((sum, r) => sum + (r.total_records || 0), 0).toLocaleString(), icon: FileText, sub: 'Data point scale' },
          { label: 'Analysis Types', value: new Set(results.map(r => r.analysis_type)).size, icon: LayoutDashboard, color: 'text-indigo-600', sub: 'Cross-module' },
        ].map((stat, i) => (
          <Card key={i} className="border-slate-200 hover:shadow-md transition-all">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-[10px] font-black uppercase tracking-widest text-slate-400">{stat.label}</CardTitle>
              <stat.icon className={`h-4 w-4 ${stat.color || 'text-slate-400'}`} />
            </CardHeader>
            <CardContent>
              <div className={`text-2xl font-bold tracking-tight ${stat.color || 'text-slate-900'}`}>{stat.value}</div>
              <p className="text-[10px] text-slate-400 font-medium mt-1">{stat.sub}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      {/* Filters */}
      <Card className="border-slate-200 shadow-sm bg-slate-50/30">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex-1 min-w-64">
              <div className="relative">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Filter by filename, user, or summary parameters..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 h-9 border-slate-200 focus:ring-indigo-500 bg-white"
                  data-testid="search-input"
                />
              </div>
            </div>
            
            <Select value={selectedType} onValueChange={setSelectedType}>
              <SelectTrigger className="w-64 h-9 border-slate-200 bg-white">
                <SelectValue placeholder="All Analysis Modules" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Global Workspace</SelectItem>
                {analysisTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      <div className="space-y-4">
        {filteredResults.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12">
              <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Results Found</h3>
              <p className="text-gray-600">
                {results.length === 0 
                  ? 'No analysis results yet. Upload and analyze files to see results here.'
                  : 'No results match your current filters. Try adjusting your search criteria.'
                }
              </p>
            </CardContent>
          </Card>
        ) : (
          filteredResults.map((result) => {
            const isExpanded = expandedResults.has(result.id);
            const hasIssues = result.issues_found > 0;
            
            return (
              <Card key={result.id} className="border-slate-200 overflow-hidden group">
                <CardHeader className="pb-4 bg-white">
                  <div className="flex items-center justify-between">
                    <div className="flex items-start gap-4 flex-1">
                      <div className="p-2.5 bg-indigo-50 rounded-xl group-hover:bg-indigo-600 transition-colors">
                        {getTypeIcon(result.analysis_type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <CardTitle className="text-md font-bold text-slate-900 group-hover:text-indigo-600 transition-colors">
                            {formatAnalysisType(result.analysis_type)}
                          </CardTitle>
                          <Badge 
                            variant="outline" 
                            className={`text-[9px] font-black uppercase tracking-widest ${getTypeColor(result.analysis_type)}`}
                          >
                            {result.analysis_type.replace('_', ' ')}
                          </Badge>
                          {hasIssues && (
                            <Badge className="bg-rose-50 text-rose-700 border-rose-200 text-[9px] font-black uppercase">
                              {result.issues_found} ANOMALIES
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-4 text-[11px] font-medium text-slate-400 truncate">
                          <span className="flex items-center gap-1.5">
                            <FileText className="h-3 w-3" />
                            {result.file_names?.join(', ')}
                          </span>
                          <span className="flex items-center gap-1.5">
                            <User className="h-3 w-3" />
                            {result.username}
                          </span>
                          <span className="flex items-center gap-1.5">
                            <Clock className="h-3 w-3" />
                            {new Date(result.created_at).toLocaleString('en-IN', {
                                day: 'numeric',
                                month: 'short',
                                hour: '2-digit',
                                minute: '2-digit'
                            })}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                       <Badge variant="outline" className="text-[10px] bg-slate-50 border-slate-200 text-slate-500 font-bold">
                         {result.total_records.toLocaleString()} ROWS
                       </Badge>
                      <Button
                        variant="soft"
                        className="h-8 gap-2 bg-indigo-50 text-indigo-600 hover:bg-indigo-600 hover:text-white text-xs font-bold"
                        onClick={() => toggleExpanded(result.id)}
                      >
                        {isExpanded ? <ChevronDown className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                        {isExpanded ? 'Collapse' : 'Inspect'}
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                
                <CardContent className="pt-0">
                  <div className={`p-3 rounded-lg border flex items-center gap-3 ${hasIssues ? 'border-rose-100 bg-rose-50/30' : 'border-emerald-100 bg-emerald-50/30'}`}>
                    {hasIssues ? (
                      <AlertTriangle className="h-4 w-4 text-rose-600 shrink-0" />
                    ) : (
                      <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
                    )}
                    <p className={`text-xs font-medium ${hasIssues ? 'text-rose-800' : 'text-emerald-800'}`}>
                      <span className="font-bold uppercase tracking-wider text-[10px] mr-2">Executive Summary:</span>
                      {result.summary}
                    </p>
                  </div>

                  {isExpanded && result.result_data && (
                    <div className="mt-4 p-4 bg-gray-50 rounded-lg" data-testid={`result-details-${result.id}`}>
                      <h4 className="font-semibold text-gray-900 mb-4">Detailed Results:</h4>
                      <div className="overflow-x-auto">
                        {renderTableData(result.result_data, result.analysis_type)}
                      </div>
                      <p className="text-xs text-gray-500 mt-4 pt-4 border-t">
                        Analysis ID: {result.id}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
};

// Helper: convert flat array to nested hierarchy
function buildHierarchy(flatData) {
  const root = [];
  const stack = [];
  flatData.forEach((row) => {
    // Ensure numeric level
    const level = parseInt(row.level) || 0;
    const newRow = { ...row, level, subRows: [], expanded: false };
    
    while (stack.length && stack[stack.length - 1].level >= newRow.level) {
      stack.pop();
    }
    
    if (stack.length === 0) {
      root.push(newRow);
    } else {
      stack[stack.length - 1].subRows.push(newRow);
    }
    stack.push(newRow);
  });
  return root;
}

export default AnomalyResults;
